import {
  CalendarDays,
  CalendarPlus,
  HeartHandshake,
  MessagesSquare,
  ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Announcement } from '../api/announcements';
import { Lockup } from '../brand/Lockup';
import { AnnouncementCard } from '../ui/AnnouncementCard';
import { BetaNotice } from '../ui/BetaNotice';
import { BUTTON_BASE } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';

/**
 * Home — what the product is, and what it becomes.
 *
 * Deliberately not a marketing hero. The spec's whole argument is that a record
 * beats a stream, so the page states the problem in one sentence and then shows
 * the record: the same 3px rule, the same announcement card that the app itself
 * uses.
 *
 * **Everything on it goes somewhere, which is the whole of TROOP-220.** The page shipped with a
 * primary button reading *See an event*, wired to `onOpenEvent` — a prop `App` passed
 * `() => undefined` once the demo routes it opened were deleted out from under it (TROOP-219). It
 * was the biggest control on the page a stranger lands on first, and the only way to find out it
 * did nothing was to press it: `Chrome.tsx`'s rule about a dead control, on the surface where it
 * costs most. It is gone rather than repointed — there is no event a signed-out visitor may read,
 * and inventing a fixture to show one would put the last piece of make-believe in the product back
 * on its front page.
 *
 * **So the ask replaces it, and it is `BetaNotice` rather than new copy.** What a stranger who
 * likes this page can actually do is write in; that component is already the one definition of how
 * (TROOP-217), rendered on About and on sign-in. A third block of prose here is the version where a
 * sentence about invitations is corrected on two screens out of three.
 *
 * **"Coming next" is gone too, and it was the other thing on the page pointing at nothing.** It
 * promised reusable lists and permission slips as future work; both shipped long ago — TROOP-31's
 * type templates and TROOP-75's three levels, TROOP-131's blank forms and TROOP-127's returned
 * ones — so the page's own roadmap was advertising the product as less finished than it is. A roadmap
 * section is a standing promise to keep it current, and nothing in the build or the tests can tell
 * you it has gone stale. *Working today* says what is true without dating itself.
 *
 * **Both buttons here are navigations, so both are anchors** wearing `BUTTON_BASE` plus a variant
 * rather than `Button`, which renders a `<button>`. The sign-in control was a `Button` calling
 * `window.location.href`, which is the same navigation with a keyboard-and-middle-click hole in it.
 *
 * **The copy is written for a Scoutmaster, and it was not** (TROOP-228). The page shipped talking
 * to whoever built it: the headline was *The answer is a field, not a message* — a field is a
 * database column — and under it, events and announcements that "hold the facts", a rule "enforced
 * in the model", and a card explaining that guardians are "a fact about two people rather than a
 * column on a child". Every one of those sentences is true of the design and meaningless to the
 * person reading it. **The domain's vocabulary is the thing this page is least able to afford**,
 * because it is the one surface where the reader has no context to translate it with, and nothing
 * in the build or the tests can tell you it reads as gibberish. The claim did not change; the words
 * did.
 *
 * **The guardian card was rewritten rather than reworded.** *Both parents, both households* was a
 * statement about the shape of the data (a guardian link is its own row, so a child has as many as
 * they have) sold as a feature, and it put a family's living arrangements on a marketing page to
 * make a point about schema. What a parent actually wants to know is that they set their child's
 * access up and can follow along — rules 11, 12 and 13a — which is true of every family regardless
 * of how many houses they keep. **It names what a guardian reads rather than claiming they see
 * everything their child sees**, because 13a's one boundary is an office the child holds: a youth
 * Quartermaster reads the gear register (rule 20) and their parent does not.
 *
 * **Plain copy overclaims where jargon hedged, and three cards did** — the audit on this ticket
 * caught all three. *Every conversation belongs to an event or an announcement* is false of
 * `ConversationAnchor.Standalone`, which is the default and covers the unit channel, every
 * subgroup's and every direct thread. *An announcement stays put at the top* is `IsPinned`, a
 * per-announcement flag, stated as though it were the type's behaviour — the sample card below
 * sets `isPinned: true`, so the page demonstrates only the case where the sentence holds. And the
 * calendar card named *a leader and a parent*, which reads as exhaustive and drops the plain adult
 * member. **The old copy hedged behind abstraction and the new copy cannot**, so every sentence
 * here is a claim somebody will hold you to: check each against the policy class before shipping
 * it, exactly as `frontend.md` says of any prose claiming who may do what.
 *
 * **The `<meta name="description">` in `index.html` is this page's copy too** and was written from
 * the old headline. It is what a search result prints and what unfurls when a Committee Chair
 * pastes the link into a group chat — the jargon's most public home, and the one surface a rewrite
 * of the JSX does not touch. It moved with this commit; `AboutScreen`'s shared sentence did too.
 *
 * "Scout"/"Scouting" appear nowhere on this page at all — not in nav, buttons or the title, and
 * since TROOP-228 not in the body copy either. Body copy describing who this serves is the one
 * permitted use (README), so a future rewrite may reach for it; nothing else here may.
 */
/**
 * Marketing copy, and labelled as such.
 *
 * This is the last thing on any screen that is not read from the API, and it is not a fixture
 * standing in for missing plumbing -- announcements are real and this page is public, so there is
 * no unit whose data could go here. It is the same component the app renders, given a written
 * example, which is the point of the section: showing that an announcement looks like state and a
 * message looks like a stream item.
 */
const SAMPLE_ANNOUNCEMENT: Announcement = {
  id: 'sample',
  title: 'Recharter forms due 15 September',
  body:
    'Every family needs to confirm contact details before recharter. It takes two minutes and we ' +
    'cannot file until everyone has done it.',
  authorPersonId: 'sample',
  authorDisplayName: 'Marcus Boone',
  authorRole: 'Committee Chair',
  isPinned: true,
  publishedAt: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
  conversationId: null,
  // No thread behind a sample, so no replies to count. It used to be 1 to draw the eyebrow's dot,
  // which now means only *new* (TROOP-268) -- so that is what this example says instead.
  unreadCount: 0,
  attachments: [],
  isNew: true,
};

export function HomeScreen() {
  return (
    <Page>
      {/* Masthead. The unit name renders as static text beside the mark because
          this member has one membership — the switcher does not mount at all
          (spec §6). No chevron, no tap target. */}
      <header style={{ background: 'var(--color-surface-inverse)' }}>
        <Gutter>
          <div
            className="flex items-center justify-between gap-4"
            style={{ paddingBlock: '13px', color: 'var(--color-text-on-inverse)' }}
          >
            <Lockup size={30} monochrome />

            {/* No unit name here. This page is public, and naming a unit to a
                signed-out visitor implies they are in it. The unit switcher rules
                in §6 are about a signed-in member's units; a stranger has none. */}
            <a
              className="t-caption"
              href="/sign-in"
              style={{ color: 'var(--color-text-on-inverse)', opacity: 0.85 }}
            >
              Sign in
            </a>
          </div>
        </Gutter>
      </header>

      {/* ---- The claim, ending in the same structural rule ---- */}
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-7)' }}>
            <p
              className="t-eyebrow"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              For troops, packs and crews
            </p>

            <h1
              className="t-display"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-3) 0 var(--space-4)',
                textWrap: 'balance',
                maxWidth: '18ch',
              }}
            >
              Stop answering the same question twice
            </h1>

            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '54ch' }}
            >
              In a group chat, &ldquo;what time do we leave Friday&rdquo; ends up under two hundred
              messages about who is bringing the griddle. TroopHerald keeps the details on the event
              itself, where every family can find them, and puts the conversation underneath.
            </p>

            {/* Both are real navigations, so both are anchors carrying the design system's own
                button geometry. The first is an in-page jump to the ask at the foot of the page —
                on a phone that card is three screens down, and the whole point of this section is
                that a stranger who reads the claim and agrees with it has somewhere to go.

                No icon on either, which took two goes. The envelope from `BetaNotice` reads as the
                obvious one and is wrong twice over: it speaks for the action one step further
                along than this control performs — press this and the page moves, press the one it
                lands on and a mail client opens, which is TROOP-135's rule about an icon speaking
                for the wrong half of a control — and the honest alternative, a downward arrow, is
                not on §7's list, which is closed and says to ask before adding to it. A button
                whose label is already a verb phrase does not need one. */}
            <div className="flex flex-wrap gap-3" style={{ marginTop: 'var(--space-6)' }}>
              <a href="#beta" className={`${BUTTON_BASE} th-btn--primary`}>
                Ask for an invite
              </a>
              <a href="/sign-in" className={`${BUTTON_BASE} th-btn--secondary`}>
                Sign in
              </a>
            </div>
          </div>
        </Gutter>
      </section>

      {/* ---- Working today ---- */}
      <section style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-7)' }}>
            <h2 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
              Working today
            </h2>

            <div
              className="grid gap-4"
              style={{
                marginTop: 'var(--space-5)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 15rem), 1fr))',
              }}
            >
              <Feature
                icon={CalendarDays}
                title="Every detail in one place"
                body="When you leave, where you are going, who is driving and what to bring — all on the event itself. Change it once and every family sees the change."
              />
              <Feature
                icon={MessagesSquare}
                title="Chat that stays on topic"
                body="An event carries its own conversation, so the questions about Friday sit with Friday — not in one channel that holds everything at once."
              />
              <Feature
                icon={ShieldCheck}
                title="Two adults, always"
                body="A conversation with young people in it cannot drop below two adults. TroopHerald keeps count so nobody else has to."
              />
              <Feature
                icon={CalendarPlus}
                title="On the calendar you already use"
                body="Subscribe once and the dates land in your phone's own calendar — every unit you are in, and every unit your children are in."
              />
              <Feature
                icon={HeartHandshake}
                title="Parents hold the keys"
                body="A young person joins through their parent, who sets the account up and can follow their child's messages, events and permission slips."
              />
            </div>
          </div>
        </Gutter>
      </section>

      {/* ---- The record, shown rather than described ---- */}
      <section>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-7)' }}>
            <h2 className="t-heading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
              What a unit sees
            </h2>
            <p
              className="t-body"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-2) 0 var(--space-5)',
                maxWidth: '54ch',
              }}
            >
              Something everyone needs to know is an announcement, not another message in the
              scroll. It looks different, it can be pinned to the top, and nobody has to work out
              which is which.
            </p>

            <AnnouncementCard announcement={SAMPLE_ANNOUNCEMENT} />
          </div>
        </Gutter>
      </section>

      {/* ---- The ask. Last, because it is the one thing on this page anybody is asked to act on,
              and it is where the hero's primary button lands. `BetaNotice` renders its own
              heading, so this section carries none of its own. ---- */}
      <section id="beta" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-7)', maxWidth: '46rem' }}>
            <BetaNotice />
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function Feature({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <Icon size={24} aria-hidden="true" style={{ color: 'var(--color-brand-ink)' }} />
      <h3
        className="t-subheading"
        style={{
          color: 'var(--color-text-primary)',
          margin: 'var(--space-3) 0 var(--space-2)',
          textWrap: 'balance',
        }}
      >
        {title}
      </h3>
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {body}
      </p>
    </div>
  );
}
